import { useMemo, useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import {
  ExclamationTriangleIcon,
  ArrowUturnLeftIcon,
  HandRaisedIcon,
  RocketLaunchIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { AdminFleetMap, AppBadge, Badge, Btn, Card, cx } from '../../../components/admin/console';
import { useApps, useOtaDevices, usePointers } from './data/hooks';
import {
  OTA_RELEASE_CHANNEL_ORDER,
  channelBadgeTone,
  channelLabel,
} from './data/channelLabels';
import type {
  DeviceConnectivity,
  OtaAppId,
  OtaChannelId,
  OtaDevice,
} from './data/types';

const VERSION_COLOR: Record<number, string> = {
  213: '#22d3ee',
  211: '#f59e0b',
  209: '#94a3b8',
  208: '#64748b',
};
const versionColor = (vc: number) => VERSION_COLOR[vc] ?? '#64748b';

const CHANNELS: OtaChannelId[] = [...OTA_RELEASE_CHANNEL_ORDER];

interface FilterState {
  conn: DeviceConnectivity | 'all';
  version: 'all' | string;
  underway: boolean;
  offline: boolean;
  q: string;
}

export function OtaCoveragePage() {
  const { data: apps = [] } = useApps();
  const { data: devices = [] } = useOtaDevices();
  const { data: pointers = [] } = usePointers();
  const [selectedApp, setSelectedApp] = useState<OtaAppId>('vn');
  const [channel, setChannel] = useState<OtaChannelId>('general');
  const [filters, setFilters] = useState<FilterState>({
    conn: 'all',
    version: 'all',
    underway: false,
    offline: false,
    q: '',
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const versions = useMemo(() => {
    if (selectedApp !== 'vn') return [];
    const counts = new Map<number, number>();
    for (const d of devices) counts.set(d.vnVc, (counts.get(d.vnVc) ?? 0) + 1);
    return [...counts.entries()]
      .map(([vc, count]) => ({ vc, count }))
      .sort((a, b) => b.vc - a.vc);
  }, [devices, selectedApp]);

  const filteredDevices = useMemo(() => {
    return devices.filter((d) => {
      if (filters.conn !== 'all' && d.connectivity !== filters.conn) return false;
      if (filters.version !== 'all' && String(d.vnVc) !== filters.version) return false;
      if (filters.underway && !d.underway) return false;
      if (filters.offline && d.offlineDays < 7) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        if (!d.name.toLowerCase().includes(q) && !d.id.includes(q)) return false;
      }
      return true;
    });
  }, [devices, filters]);

  const generalRollout = pointers.find(
    (p) => p.app === selectedApp && p.channel === 'general',
  );
  const showHaltedBanner = devices.some((d) => d.autoRollback) && selectedApp === 'vn' && channel === 'general';

  const app = apps.find((a) => a.id === selectedApp);

  return (
    <div className="px-8 py-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400">
            Fleet Coverage
          </div>
          <h1 className="text-2xl font-bold text-white mt-1 flex items-center gap-3">
            <AppBadge app={selectedApp} size="md" />
            Coverage · {app?.name ?? selectedApp}
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            The decision view. Is the served cohort representative enough to widen?
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 p-1 rounded-lg bg-slate-800/60 border border-slate-700">
            {apps.map((a) => (
              <button
                type="button"
                key={a.id}
                onClick={() => setSelectedApp(a.id)}
                className={cx(
                  'px-2.5 py-1 rounded-md text-xs font-semibold font-mono flex items-center gap-1.5',
                  selectedApp === a.id ? 'bg-cyan-500/20 text-cyan-300' : 'text-slate-400 hover:text-white',
                )}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: a.color }} />
                {a.id}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 p-1 rounded-lg bg-slate-800/60 border border-slate-700">
            {CHANNELS.map((c) => (
              <button
                type="button"
                key={c}
                onClick={() => setChannel(c)}
                className={cx(
                  'px-2.5 py-1 rounded-md text-xs font-semibold',
                  channel === c ? 'bg-cyan-500/20 text-cyan-300' : 'text-slate-400 hover:text-white',
                )}
              >
                {channelLabel(c)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {showHaltedBanner && generalRollout && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-red-500/20 text-red-400 flex items-center justify-center">
            <ExclamationTriangleIcon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-white">
              haltOnAnyRollback triggered — one device auto-rolled back from{' '}
              {generalRollout.current} → {generalRollout.previous}
            </div>
            <p className="text-xs text-slate-300 mt-1">
              Rollout frozen at {generalRollout.served}/{generalRollout.maxDevices}. Inspect
              the flagged device below, then roll back the pointer or resume after triage.
            </p>
          </div>
          <Btn
            variant="danger"
            size="sm"
            leftIcon={<ArrowUturnLeftIcon className="w-3 h-3" />}
          >
            Rollback to {generalRollout.previous}
          </Btn>
        </div>
      )}

      <Card padded={false} className="overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-700/60 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Primary data
            </div>
            <h2 className="text-sm font-bold text-white">Versions on {channel}</h2>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-800/40">
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              <th className="text-left px-5 py-2.5">Version</th>
              <th className="text-left px-2 py-2.5">Status</th>
              <th className="text-right px-2 py-2.5">Devices</th>
              <th className="text-right px-2 py-2.5">Avg days</th>
              <th className="text-right px-2 py-2.5">Crashes 7d</th>
              <th className="text-right px-2 py-2.5">Auto-rollbacks</th>
              <th className="text-left px-2 py-2.5">Coverage signal</th>
              <th className="text-right px-5 py-2.5">Action</th>
            </tr>
          </thead>
          <tbody>
            <VersionRow
              vc={213}
              status={{ label: 'candidate (in test)', tone: 'warning' }}
              devices={2}
              avgDays={2}
              crashes={1}
              rollbacks={0}
              signal={['dev-bench only']}
              action={
                <Btn
                  size="sm"
                  variant="primary"
                  leftIcon={<RocketLaunchIcon className="w-3 h-3" />}
                >
                  Promote…
                </Btn>
              }
            />
            <VersionRow
              vc={211}
              status={
                showHaltedBanner
                  ? { label: 'rolling · halted', tone: 'error' }
                  : { label: 'rolling', tone: 'warning' }
              }
              devices={showHaltedBanner ? 3 : 4}
              avgDays={1}
              crashes={showHaltedBanner ? 4 : 0}
              rollbacks={showHaltedBanner ? 1 : 0}
              signal={['3× weekend WiFi', '1× Scout at-sea']}
              flagged={showHaltedBanner}
              action={
                showHaltedBanner ? (
                  <div className="flex justify-end gap-1">
                    <Btn size="sm" variant="warning" disabled>
                      Halted
                    </Btn>
                    <Btn size="sm" variant="danger">
                      Rollback
                    </Btn>
                  </div>
                ) : (
                  <div className="flex justify-end gap-1">
                    <Btn size="sm" variant="outline">
                      Allow 5 more
                    </Btn>
                    <Btn size="sm" variant="outline">
                      Open to fleet
                    </Btn>
                    <Btn size="sm" variant="warning" leftIcon={<HandRaisedIcon className="w-3 h-3" />}>
                      Halt
                    </Btn>
                  </div>
                )
              }
            />
            <VersionRow
              vc={209}
              status={{ label: 'previous', tone: 'neutral' }}
              devices={14}
              avgDays={14}
              crashes={0}
              rollbacks={0}
              signal={['mixed cellular/WiFi', '3 regions']}
              action={<span className="text-slate-600 text-xs">—</span>}
            />
            <VersionRow
              vc={208}
              status={{ label: 'previous', tone: 'neutral' }}
              devices={19}
              avgDays={21}
              crashes={0}
              rollbacks={0}
              signal={['broad', 'all connectivity']}
              action={<span className="text-slate-600 text-xs">—</span>}
            />
          </tbody>
        </table>
      </Card>

      <Card padded={false} className="overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-700/60">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Fleet map
          </div>
          <h2 className="text-sm font-bold text-white">Devices across NZ & the Pacific</h2>
        </div>
        <FleetMap devices={filteredDevices} />
      </Card>

      <Card padded={false} className="overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-700/60 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 p-0.5 rounded-lg bg-slate-900/60 border border-slate-700">
            {(['all', 'wifi', 'cellular', 'satellite', 'none'] as const).map((c) => (
              <button
                type="button"
                key={c}
                onClick={() => setFilters((f) => ({ ...f, conn: c }))}
                className={cx(
                  'px-2 py-1 rounded-md text-[11px] font-semibold',
                  filters.conn === c ? 'bg-cyan-500/20 text-cyan-300' : 'text-slate-400 hover:text-white',
                )}
              >
                {c}
              </button>
            ))}
          </div>
          <select
            value={filters.version}
            onChange={(e) => setFilters((f) => ({ ...f, version: e.target.value }))}
            className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white font-mono"
          >
            <option value="all">all versions</option>
            {versions.map((v) => (
              <option key={v.vc} value={v.vc}>
                vc {v.vc} ({v.count})
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={filters.underway}
              onChange={(e) => setFilters((f) => ({ ...f, underway: e.target.checked }))}
              className="rounded border-slate-600 bg-slate-900 text-cyan-500"
            />
            underway now
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={filters.offline}
              onChange={(e) => setFilters((f) => ({ ...f, offline: e.target.checked }))}
              className="rounded border-slate-600 bg-slate-900 text-cyan-500"
            />
            offline &gt; 7d
          </label>
          <div className="relative">
            <MagnifyingGlassIcon className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              placeholder="vessel / id"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              className="pl-7 pr-2 py-1 w-48 bg-slate-900/60 border border-slate-700 rounded-md text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
            />
          </div>
          <div className="flex-1" />
          {selected.size > 0 && (
            <>
              <span className="text-[11px] text-cyan-400 font-semibold">
                {selected.size} selected
              </span>
              <Btn size="sm" variant="outline">+ allowList</Btn>
              <Btn size="sm" variant="outline">+ denyList</Btn>
              <Btn size="sm" variant="ghost" leftIcon={<MapPinIcon className="w-3 h-3" />}>
                Pin…
              </Btn>
            </>
          )}
        </div>
        <div className="max-h-[480px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/60 sticky top-0">
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                <th className="w-8 px-3 py-2.5"></th>
                <th className="text-left px-2 py-2.5">Vessel</th>
                <th className="text-left px-2 py-2.5">Device</th>
                <th className="text-left px-2 py-2.5">Channel</th>
                <th className="text-right px-2 py-2.5">vn vc</th>
                <th className="text-left px-2 py-2.5">Conn</th>
                <th className="text-left px-2 py-2.5">Role</th>
                <th className="text-left px-2 py-2.5">Underway</th>
                <th className="text-right px-2 py-2.5">Crashes</th>
                <th className="text-right px-3 py-2.5">Last check-in</th>
              </tr>
            </thead>
            <tbody>
              {filteredDevices.slice(0, 60).map((d) => (
                <DeviceRow
                  key={d.id}
                  d={d}
                  checked={selected.has(d.id)}
                  onToggle={() => {
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (next.has(d.id)) next.delete(d.id);
                      else next.add(d.id);
                      return next;
                    });
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function VersionRow({
  vc,
  status,
  devices,
  avgDays,
  crashes,
  rollbacks,
  signal,
  action,
  flagged,
}: {
  vc: number;
  status: { label: string; tone: 'success' | 'warning' | 'error' | 'neutral' | 'info' | 'blue' | 'admin' };
  devices: number;
  avgDays: number;
  crashes: number;
  rollbacks: number;
  signal: string[];
  action: React.ReactNode;
  flagged?: boolean;
}) {
  return (
    <tr
      className={cx(
        'border-t border-slate-700/40 density-row',
        flagged && 'bg-red-500/5',
      )}
    >
      <td className="px-5 py-3">
        <div className="flex items-baseline gap-2">
          <span className="text-slate-500 text-[10px] font-mono">vc</span>
          <span className="text-xl font-bold font-mono text-white">{vc}</span>
        </div>
      </td>
      <td className="px-2 py-3">
        <Badge tone={status.tone} dot>
          {status.label}
        </Badge>
      </td>
      <td className="px-2 py-3 text-right font-mono text-white">{devices}</td>
      <td className="px-2 py-3 text-right font-mono text-slate-400">{avgDays}d</td>
      <td className="px-2 py-3 text-right font-mono">
        <span className={crashes > 0 ? 'text-red-400' : 'text-slate-600'}>{crashes}</span>
      </td>
      <td className="px-2 py-3 text-right font-mono">
        <span className={rollbacks > 0 ? 'text-red-400' : 'text-slate-600'}>{rollbacks}</span>
      </td>
      <td className="px-2 py-3">
        <div className="flex flex-wrap gap-1">
          {signal.map((s) => (
            <span
              key={s}
              className="px-1.5 py-0.5 rounded bg-slate-700/40 text-[10px] text-slate-300 border border-slate-600"
            >
              {s}
            </span>
          ))}
        </div>
      </td>
      <td className="px-5 py-3 text-right">{action}</td>
    </tr>
  );
}

function DeviceRow({
  d,
  checked,
  onToggle,
}: {
  d: OtaDevice;
  checked: boolean;
  onToggle: () => void;
}) {
  const color = versionColor(d.vnVc);
  return (
    <tr
      className={cx(
        'border-t border-slate-700/40 hover:bg-slate-700/20 density-row',
        d.autoRollback && 'bg-red-500/5',
      )}
    >
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="rounded border-slate-600 bg-slate-900 text-cyan-500"
        />
      </td>
      <td className="px-2 py-2">
        <div className="text-xs font-semibold text-white">{d.name}</div>
        <div className="text-[10px] text-slate-500">{d.cluster}</div>
      </td>
      <td className="px-2 py-2 text-[11px] font-mono text-slate-400">
        {d.id}
        <div className="text-[10px] text-slate-600">{d.model}</div>
      </td>
      <td className="px-2 py-2">
        <Badge tone={channelBadgeTone(d.channel)}>
          {channelLabel(d.channel)}
        </Badge>
      </td>
      <td className="px-2 py-2 text-right">
        <span
          className="inline-flex items-center gap-1.5 font-mono text-sm font-bold"
          style={{ color }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
          {d.vnVc}
        </span>
        {d.autoRollback && d.flaggedBuild && (
          <div className="text-[9px] text-red-400 font-semibold mt-0.5">
            auto-rolled back from {d.flaggedBuild}
          </div>
        )}
      </td>
      <td className="px-2 py-2 text-[11px] text-slate-400">{d.connectivity}</td>
      <td className="px-2 py-2">
        <Badge
          tone={
            d.role === 'admin' ? 'admin' : d.role === 'tester_plus' ? 'warning' : 'neutral'
          }
        >
          {d.role}
        </Badge>
      </td>
      <td className="px-2 py-2 text-[11px] text-slate-400 font-mono">
        {d.underway ? `${d.sog}kn` : '—'}
      </td>
      <td className="px-2 py-2 text-right font-mono">
        {d.crashes > 0 ? (
          <span className="text-red-400">{d.crashes}</span>
        ) : (
          <span className="text-slate-600">0</span>
        )}
      </td>
      <td className="px-3 py-2 text-right text-[10px] text-slate-500 font-mono">
        {d.lastCheckIn}
      </td>
    </tr>
  );
}

function Legend({
  shape,
  color,
  label,
}: {
  shape: 'wifi' | 'cell' | 'sat' | 'none';
  color: string;
  label: string;
}) {
  const style: React.CSSProperties = {
    background: color,
    boxShadow: '0 0 0 1.5px rgba(15,23,42,0.9)',
    transform: shape === 'sat' ? 'rotate(45deg)' : undefined,
    opacity: shape === 'none' ? 0.5 : 1,
  };
  return (
    <span className="flex items-center gap-1.5">
      <span
        className={cx(
          'inline-block w-3 h-3',
          shape === 'wifi' && 'rounded-full',
          shape === 'cell' && 'rounded-[2px]',
        )}
        style={style}
      />
      {label}
    </span>
  );
}

function deviceMarkerIcon(d: OtaDevice): L.DivIcon {
  const color = versionColor(d.vnVc);
  const shape =
    d.connectivity === 'wifi' ? 'wifi'
    : d.connectivity === 'cellular' ? 'cell'
    : d.connectivity === 'satellite' ? 'sat'
    : 'none';
  const ringStyle = d.autoRollback
    ? 'box-shadow:0 0 0 2px #ef4444, 0 0 12px #ef4444;'
    : '';
  const baseStyle =
    'display:flex;align-items:center;justify-content:center;width:18px;height:18px;border:2px solid rgba(15,23,42,0.9);';
  const shapeStyle =
    shape === 'wifi' ? 'border-radius:50%;'
    : shape === 'cell' ? 'border-radius:3px;'
    : shape === 'sat'  ? 'transform:rotate(45deg);'
    : 'border-radius:50%;opacity:0.55;filter:grayscale(0.6);';
  return L.divIcon({
    className: '',
    iconSize: [22, 22],
    html: `<div style="${baseStyle}${shapeStyle}background:${color};${ringStyle}"></div>`,
  });
}

function FleetMap({ devices }: { devices: OtaDevice[] }) {
  return (
    <div style={{ height: 420, width: '100%' }} className="relative bg-slate-950">
      <AdminFleetMap>
        {devices.map((d) => (
          <Marker key={d.id} position={[d.lat, d.lng]} icon={deviceMarkerIcon(d)}>
            <Popup>
              <div style={{ fontFamily: 'Inter, sans-serif', minWidth: 180 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'white' }}>{d.name}</div>
                <div
                  style={{
                    fontSize: 10,
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginTop: 2,
                  }}
                >
                  {d.cluster}
                </div>
                <div
                  style={{
                    marginTop: 8,
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: '2px 8px',
                    fontSize: 11,
                  }}
                >
                  <span style={{ color: '#64748b' }}>version</span>
                  <span style={{ color: '#e2e8f0', fontFamily: 'JetBrains Mono' }}>
                    vn vc {d.vnVc}
                  </span>
                  <span style={{ color: '#64748b' }}>channel</span>
                  <span style={{ color: '#e2e8f0' }}>{d.channel}</span>
                  <span style={{ color: '#64748b' }}>connectivity</span>
                  <span style={{ color: '#e2e8f0' }}>{d.connectivity}</span>
                  <span style={{ color: '#64748b' }}>last check-in</span>
                  <span style={{ color: '#e2e8f0' }}>{d.lastCheckIn}</span>
                  {d.autoRollback && (
                    <span style={{ color: '#f87171', gridColumn: '1 / 3', marginTop: 4 }}>
                      ⚠ auto-rolled back after {d.crashes} crashes
                    </span>
                  )}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </AdminFleetMap>

      {/* Legend overlay — matches /admin/fleet-map placement */}
      <div className="absolute bottom-4 left-4 z-[1000] bg-slate-900/90 backdrop-blur-sm border border-slate-700 rounded-lg px-3 py-2">
        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">
          Connectivity
        </div>
        <ul className="space-y-1">
          <li className="flex items-center gap-2 text-[11px] text-slate-300">
            <Legend shape="wifi" color="#22d3ee" label="WiFi" />
          </li>
          <li className="flex items-center gap-2 text-[11px] text-slate-300">
            <Legend shape="cell" color="#34d399" label="Cellular" />
          </li>
          <li className="flex items-center gap-2 text-[11px] text-slate-300">
            <Legend shape="sat" color="#c084fc" label="Satellite" />
          </li>
          <li className="flex items-center gap-2 text-[11px] text-slate-300">
            <Legend shape="none" color="#64748b" label="Offline" />
          </li>
        </ul>
        <div className="mt-2 pt-2 border-t border-slate-700/60 text-[9px] font-bold uppercase tracking-widest text-slate-500">
          Version cohort
        </div>
        <ul className="space-y-1 mt-1.5">
          <li className="flex items-center gap-2 text-[11px] text-slate-300">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#22d3ee' }} />
            vc 213 (test)
          </li>
          <li className="flex items-center gap-2 text-[11px] text-slate-300">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#f59e0b' }} />
            vc 211 (rolling)
          </li>
          <li className="flex items-center gap-2 text-[11px] text-slate-300">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#94a3b8' }} />
            vc 209 (previous)
          </li>
          <li className="flex items-center gap-2 text-[11px] text-slate-300">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#64748b' }} />
            vc 208 (previous)
          </li>
        </ul>
      </div>
    </div>
  );
}

export default OtaCoveragePage;
